import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {IconButton, Paper, Tooltip} from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {AdminI18N, CommonI18N, CompanyI18N} from "@/translations";
import {
    CompanyRelationshipActions,
    CompanyRelationshipFilterDto,
    CompanyRelationshipStatus,
    CompanyWithRelationshipDTO,
    Country
} from "@/api/data-contracts";
import CompanyFilterBar from "./CompanyFilterBar";
import CompanyStatusChip from "./CompanyStatusChip";
import {useDialogController} from "@/lib/dialog-controller";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {isClientManagingDirectorRole, useAuth} from "@/app/auth/AuthContext";
import {AppFrame} from "@/app/AppFrame";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {DataTable} from "@/lib/components/DataTable";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {companyApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {DateFormatter} from "@/lib/components/DateFormate";

type CountryFilter = Country | "ALL";

const CompaniesListPage: React.FC = () => {
    const {t} = useTranslation();
    const {roles, company} = useAuth();

    const [DialogHost, showDialog] = useDialogController();
    const {showMessage} = useSnackbarEx();

    const [rows, setRows] = useState<CompanyWithRelationshipDTO[]>([]);

    const [filters, setFilters] = useState<{
        name: string;
        status?: CompanyRelationshipStatus;
        country: CountryFilter;
    }>({
        name: "",
        status: undefined,
        country: "ALL"
    });

    const [nameInput, setNameInput] = useState("");
    const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const sourceCompanyId = company?.id;

    const loadRelationships = useCallback(async () => {
        if (!sourceCompanyId) {
            return;
        }

        const filterBody: CompanyRelationshipFilterDto | undefined =
                filters.name || filters.status || filters.country !== "ALL"
                        ? {
                            companyName: filters.name || undefined,
                            status: filters.status,
                            country: filters.country === "ALL" ? undefined : filters.country
                        }
                        : undefined;

        try {
            const res = await companyApi.getAllRelationship(
                    sourceCompanyId,
                    {pageable: {page: 0, size: 600}},
                    filterBody as CompanyRelationshipFilterDto
            );
            const data = res.data;
            const companyRows = data.companyWithRelationshipDTOS || [];
            setRows(companyRows);
        } catch (error: unknown) {
            showMessage({severity: "error", summary: t(AdminI18N.companyRelationships.message.loadFailed), error});
        }
    }, [sourceCompanyId, filters.name, filters.country, filters.status, showMessage, t]);

    useEffect(() => {
        loadRelationships();
    }, [loadRelationships]);

    const onToggleListState = (row: CompanyWithRelationshipDTO) => {
        if (!sourceCompanyId || !row.targetCompanyId || !row.relationship) {
            return;
        }

        const allowed = row.relationship.allowedActions || [];
        const nextAction = allowed[0];
        if (!nextAction) {
            return;
        }

        const messageKeyMap: Record<string, string> = {
            [CompanyRelationshipActions.WHITELIST]: AdminI18N.companyRelationships.dialog.confirmMessageWhitelist,
            [CompanyRelationshipActions.BLACKLIST]: AdminI18N.companyRelationships.dialog.confirmMessageBlacklist,
            [CompanyRelationshipActions.DESIRED_WHITELIST]:
            AdminI18N.companyRelationships.dialog.confirmMessageDesiredWhitelist
        };
        const messageKey =
                messageKeyMap[nextAction] ?? AdminI18N.companyRelationships.dialog.confirmMessageWithdrawDesiredWhitelist;

        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            const payload: CompanyRelationshipStatus =
                                    nextAction === CompanyRelationshipActions.WHITELIST ||
                                    nextAction === CompanyRelationshipActions.DESIRED_WHITELIST
                                            ? CompanyRelationshipStatus.WHITELIST
                                            : CompanyRelationshipStatus.BLACKLIST;

                            try {
                                await companyApi.setRelationship(sourceCompanyId, row.targetCompanyId!, payload);
                                await loadRelationships();
                                showMessage({
                                    severity: "success",
                                    summary: t(AdminI18N.companyRelationships.message.updateSuccess, {
                                        companyName: row.targetCompanyName
                                    })
                                });
                            } catch (error: unknown) {
                                showMessage({
                                    severity: "error",
                                    summary: t(AdminI18N.companyRelationships.message.updateFailed),
                                    error
                                });
                            } finally {
                                close();
                            }
                        }}
                        title={t(AdminI18N.companyRelationships.dialog.confirmTitle)}
                        message={t(messageKey, {companyName: row.targetCompanyName})}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                        dataCy="company-toggle-liststate-confirm"
                        closeOnConfirm
                />
        ));
    };

    const handleFilterChange = (
            patch: Partial<{ name: string; status?: CompanyRelationshipStatus; country: CountryFilter }>
    ) => {
        if (Object.hasOwn(patch, "name")) {
            const nextName = patch.name ?? "";
            setNameInput(nextName);

            if (nameDebounceRef.current !== undefined) {
                clearTimeout(nameDebounceRef.current);
            }

            nameDebounceRef.current = setTimeout(() => {
                setFilters((f) => ({...f, name: nextName}));
            }, 250);
            return;
        }

        setFilters((f) => ({...f, ...patch}));
    };

    useEffect(
            () => () => {
                if (nameDebounceRef.current !== undefined) {
                    clearTimeout(nameDebounceRef.current);
                }
            },
            []
    );

    const canEdit = isClientManagingDirectorRole(roles);

    return (
            <AppFrame title={t(AdminI18N.companyRelationships.page.title)} padding={2}>
                <DialogHost/>
                <CompanyFilterBar
                        name={nameInput}
                        status={filters.status}
                        country={filters.country}
                        onChange={handleFilterChange}
                />
                <Paper>
                    <CompaniesTable
                            rows={rows}
                            canEdit={canEdit}
                            onToggleListState={onToggleListState}
                            noResults={rows.length === 0 ? t(AdminI18N.companies.message.notFound) : ""}
                    />
                </Paper>
            </AppFrame>
    );
};

export default withAuthWrapperComponent(CompaniesListPage);

const CompanyTargetNameCell: React.FC<{ info: CellContext<CompanyWithRelationshipDTO, string> }> = ({info}) => (
    <span>{info.row.original.targetCompanyName}</span>
);

const CompanyRelationshipStatusCell: React.FC<{ info: CellContext<CompanyWithRelationshipDTO, string> }> = ({
                                                                                                                info
                                                                                                            }) =>
        <CompanyStatusChip relationship={info.row.original.relationship}/>;

type CompanyActionsProps = {
    info: CellContext<CompanyWithRelationshipDTO, string>;
    onToggleListState: (row: CompanyWithRelationshipDTO) => void;
};

const CompanyRelationshipActionsCell: React.FC<CompanyActionsProps> = ({info, onToggleListState}) => {
    const {t} = useTranslation();
    const row = info.row.original;
    const allowed = row.relationship?.allowedActions || [];
    const action = allowed[0];
    if (!action) {
        return null;
    }

    const currentStatus = row.relationship?.currentStatus;
    const desiredStatus = row.relationship?.desiredStatus;

    let buttonColor: "success" | "warning" | "error";
    if (!currentStatus || currentStatus === CompanyRelationshipStatus.WHITELIST) {
        buttonColor = "success";
    } else if (
        currentStatus === CompanyRelationshipStatus.BLACKLIST &&
        desiredStatus === CompanyRelationshipStatus.WHITELIST
    ) {
        buttonColor = "warning";
    } else {
        buttonColor = "error";
    }

    const actionLabelKeyMap: Record<string, string> = {
        [CompanyRelationshipActions.WHITELIST]: AdminI18N.companyRelationships.action.whitelist,
        [CompanyRelationshipActions.BLACKLIST]: AdminI18N.companyRelationships.action.blacklist,
        [CompanyRelationshipActions.DESIRED_WHITELIST]: AdminI18N.companyRelationships.action.desiredWhitelist
    };
    const label = t(actionLabelKeyMap[action] ?? AdminI18N.companyRelationships.action.withdrawDesiredWhitelist);

    return (
            <div style={{textAlign: "right"}}>
            <Tooltip title={label} placement="top">
                <IconButton size="medium" color={buttonColor} aria-label={label} onClick={() => onToggleListState(row)}>
                    <HandshakeOutlinedIcon fontSize="medium"/>
                </IconButton>
            </Tooltip>
        </div>
    );
};

const renderCompanyTargetNameCell = (info: CellContext<CompanyWithRelationshipDTO, string>) => (
        <CompanyTargetNameCell info={info}/>
);

const renderCompanyRelationshipStatusCell = (info: CellContext<CompanyWithRelationshipDTO, string>) => (
        <CompanyRelationshipStatusCell info={info}/>
);

function makeCompanyActionsCell(onToggleListState: (row: CompanyWithRelationshipDTO) => void) {
    function ActionsCell(info: Readonly<CellContext<CompanyWithRelationshipDTO, string>>) {
        return <CompanyRelationshipActionsCell info={info} onToggleListState={onToggleListState}/>;
    }
    return ActionsCell;
}

type CompaniesTableProps = {
    rows: CompanyWithRelationshipDTO[];
    canEdit: boolean;
    onToggleListState: (row: CompanyWithRelationshipDTO) => void;
    noResults: string;
};

const CompaniesTable: React.FC<CompaniesTableProps> = ({rows, canEdit, onToggleListState, noResults}) => {
    const {t} = useTranslation();
    const dateFormat = DateFormatter("date");

    const baseColumns = useMemo<ColumnDef<CompanyWithRelationshipDTO, string>[]>(
            () => [
                {
                    header: t(AdminI18N.companies.column.name),
                    accessorKey: "targetCompanyName",
                    cell: renderCompanyTargetNameCell
                },
                {
                    header: t(AdminI18N.companies.column.country),
                    accessorFn: (row) =>
                            row.companyAddress
                                    ? t(CommonI18N.enum.country[row.companyAddress.country as keyof typeof CommonI18N.enum.country])
                                    : ""
                },
                {
                    header: t(CompanyI18N.form.field.city),
                    accessorFn: (row) => row.companyAddress?.city || ""
                },
                {
                    header: t(AdminI18N.companies.column.postalCode),
                    accessorFn: (row) => row.companyAddress?.zip || ""
                },
                {
                    header: t(AdminI18N.companies.column.groupName),
                    accessorFn: (row) => row.targetTenantName || ""
                },
                {
                    header: t(AdminI18N.companies.column.status),
                    meta: {disableTooltip: true},
                    cell: renderCompanyRelationshipStatusCell
                },
                {
                    header: t(AdminI18N.companyRelationships.field.blacklistedAt),
                    accessorFn: (row) => {
                        const relationship = row.relationship;
                        if (!relationship?.effectiveAt) {
                            return "";
                        }

                        if (
                                relationship.currentStatus === CompanyRelationshipStatus.BLACKLIST &&
                                relationship.desiredStatus !== CompanyRelationshipStatus.WHITELIST
                        ) {
                            return dayjs(relationship.effectiveAt).format(dateFormat);
                        }

                        return "";
                    }
                },
                {
                    header: t(AdminI18N.companyRelationships.field.effectiveAt),
                    accessorFn: (row) => {
                        const relationship = row.relationship;
                        if (!relationship) {
                            return "";
                        }

                        if (
                                relationship.currentStatus === CompanyRelationshipStatus.BLACKLIST &&
                                relationship.desiredStatus === CompanyRelationshipStatus.WHITELIST
                        ) {
                            return dayjs(relationship.effectiveAt).format(dateFormat);
                        }

                        return "";
                    }
                }
                // actions column will be conditionally added below when canEdit is true
            ],
            [t, dateFormat]
    );

    const columns = useMemo<ColumnDef<CompanyWithRelationshipDTO, string>[]>(() => {
        const base = [...baseColumns];
        if (canEdit) {
            base.push({
                id: "actions",
                header: "",
                meta: {disableTooltip: true},
                minSize: 1,
                cell: makeCompanyActionsCell(onToggleListState)
            });
        }
        return base;
    }, [canEdit, onToggleListState, baseColumns]);

    return (
            <DataTable
                    columns={columns as ColumnDef<never, string>[]}
                    data={rows as never[]}
                    noResultsValue={noResults}
                    striped
                    selectedRowId={null}
                    disableHover
            />
    );
};
